import { CUSTOM_CONSENT_FIELD_ID, Register } from "../event";
import { SonyMusicCountryOptIn } from "../artists";

/** Custom-field id for the Sony Music email opt-in checkbox. */
const SONY_OPTIN_FIELD_ID = "optin-sony";
/** Marker attribute used to identify (and re-strip) the Sony policy disclaimer. */
const SONY_DISCLAIMER_MARKER = "data-sony-policy";

/**
 * Applies the Sony Music email opt-in (required) to an event `register`, in
 * place — stripping any prior one first so it's correct across venue changes.
 * Ordered between the email (`terms`) and SMS (`sms`) opt-ins, after custom
 * consent when present. The opt-in's Terms/Privacy links are appended to the
 * form's footer `disclaimers` (mirroring how UMG adds its policies). `optIn` is
 * resolved for the venue's country by the caller (it's an async Firestore read;
 * this stays synchronous).
 */
export const applySonyOptIn = (
  register: Register,
  optIn: SonyMusicCountryOptIn | undefined,
  artistName: string,
): void => {
  if (register.customFields?.length) {
    register.customFields = register.customFields.filter(
      (f) => f.id !== SONY_OPTIN_FIELD_ID,
    );
    if (!register.customFields.length) delete register.customFields;
  }
  if (register.fieldsOrder?.length) {
    register.fieldsOrder = register.fieldsOrder.filter(
      (f) => f.id !== SONY_OPTIN_FIELD_ID,
    );
    if (!register.fieldsOrder.length) delete register.fieldsOrder;
  }
  if (register.disclaimers?.length) {
    register.disclaimers = register.disclaimers.filter(
      (d) => !d.includes(SONY_DISCLAIMER_MARKER),
    );
    if (!register.disclaimers.length) delete register.disclaimers;
  }

  if (!optIn) return;

  register.customFields = [
    ...(register.customFields ?? []),
    {
      id: SONY_OPTIN_FIELD_ID,
      type: "checkbox" as const,
      required: true,
      label: optIn.email.replace(/ARTIST/g, artistName),
    },
  ];

  const ordered: string[] = ["terms"];
  if (register.customFields.some((f) => f.id === CUSTOM_CONSENT_FIELD_ID)) {
    ordered.push(CUSTOM_CONSENT_FIELD_ID);
  }
  ordered.push(SONY_OPTIN_FIELD_ID);
  if (register.sms) ordered.push("sms");
  const rest = (register.fieldsOrder ?? []).filter(
    (f) => !ordered.includes(f.id),
  );
  register.fieldsOrder = [
    ...ordered.map((id, index) => ({ id, index })),
    ...rest.map((f, i) => ({ id: f.id, index: i + ordered.length })),
  ];

  // Append the country's Terms/Privacy links to the footer disclaimers.
  const links: string[] = [];
  if (optIn.termsUrl) {
    links.push(link(optIn.termsUrl, optIn.termsLabel ?? "Terms and Conditions"));
  }
  if (optIn.privacyUrl) {
    links.push(link(optIn.privacyUrl, optIn.privacyLabel ?? "Privacy Policy"));
  }
  if (links.length) {
    register.disclaimers = [
      ...(register.disclaimers ?? []),
      `<span ${SONY_DISCLAIMER_MARKER}>Sony Music: ${links.join(" · ")}</span>`,
    ];
  }
};

const link = (href: string, text: string): string =>
  `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;
